import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

// Convex hull — Graham scan, no dependencies
function convexHull(points) {
  if (points.length < 3) return points;
  var pivot = points.reduce(function(a, b) {
    return b[1] < a[1] || (b[1] === a[1] && b[0] < a[0]) ? b : a;
  });
  var sorted = points.slice().sort(function(a, b) {
    var ax = a[0] - pivot[0], ay = a[1] - pivot[1];
    var bx = b[0] - pivot[0], by = b[1] - pivot[1];
    var cross = ax * by - ay * bx;
    if (cross !== 0) return cross > 0 ? -1 : 1;
    return (ax * ax + ay * ay) - (bx * bx + by * by);
  });
  var hull = [];
  for (var i = 0; i < sorted.length; i++) {
    while (hull.length >= 2) {
      var a = hull[hull.length - 2];
      var b = hull[hull.length - 1];
      var c = sorted[i];
      if ((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]) <= 0)
        hull.pop();
      else break;
    }
    hull.push(sorted[i]);
  }
  return hull;
}

const TAC_PALETTE = [
  '#E53935', '#8E24AA', '#1E88E5', '#00897B', '#F4511E',
  '#6D4C41', '#3949AB', '#039BE5', '#43A047', '#FFB300',
  '#D81B60', '#5E35B1', '#00ACC1', '#7CB342', '#FB8C00',
  '#C0CA33', '#00BCD4', '#FF7043', '#26A69A', '#AB47BC',
];

const TacLayer = ({ sites, visible, selectedTitles }) => {
  const map = useMap();
  const layerGroupRef = useRef(null);
  const selectedTacRef = useRef(null); // currently selected TAC
  const tacLayersRef  = useRef({});    // tac -> { polygon, marker }

  function applySelection(selectedTac) {
    selectedTacRef.current = selectedTac;
    var layers = tacLayersRef.current;
    Object.keys(layers).forEach(function(tac) {
      var entry = layers[tac];
      if (!entry.polygon) return;
      if (selectedTac === null || tac === selectedTac) {
        // Highlighted
        entry.polygon.setStyle({ opacity: 0.9, fillOpacity: 0.15, weight: 2.5 });
        entry.marker.setOpacity(1);
      } else {
        // Dimmed
        entry.polygon.setStyle({ opacity: 0.15, fillOpacity: 0.03, weight: 1 });
        entry.marker.setOpacity(0.25);
      }
    });
  }

  useEffect(function() {
    if (!map) return;

    if (layerGroupRef.current) {
      layerGroupRef.current.clearLayers();
      map.removeLayer(layerGroupRef.current);
      layerGroupRef.current = null;
      tacLayersRef.current  = {};
      selectedTacRef.current = null;
    }

    if (!visible || !sites || sites.length === 0) return;

    // Step 1: TACs with active sites (respecting title filter)
    var titleFilter = selectedTitles && selectedTitles.length > 0 ? selectedTitles : null;
    var activeTacs = new Set();
    sites.forEach(function(site) {
      if (site._status === 'none') return;
      // Match site against title filter using _titles (all titles) when available.
      // Falls back to _title (dominant) for sites without _titles populated.
      if (titleFilter) {
        var matches = false;
        if (site._titles && site._titles.length > 0) {
          matches = site._titles.some(function(t) { return titleFilter.includes(t); });
        } else if (site._title) {
          matches = titleFilter.includes(site._title);
        }
        if (!matches) return;
      }
      var tac = site.tac != null ? site.tac : (site.tAC != null ? site.tAC : site.TAC);
      if (tac) activeTacs.add(String(tac));
    });

    // Step 2: all sites of those TACs
    var tacGroups = {};
    sites.forEach(function(site) {
      var tac = site.tac != null ? site.tac : (site.tAC != null ? site.tAC : site.TAC);
      if (!tac || !activeTacs.has(String(tac))) return;
      var lat = parseFloat(site.latitude);
      var lng = parseFloat(site.longitude);
      if (isNaN(lat) || isNaN(lng)) return;
      if (!tacGroups[tac]) tacGroups[tac] = [];
      tacGroups[tac].push([lng, lat]);
    });

    var layerGroup = L.layerGroup().addTo(map);
    layerGroupRef.current = layerGroup;
    tacLayersRef.current  = {};

    // Compute a size metric per TAC (bounding-box area) so we can render
    // larger polygons first and smaller ones on top — smaller polygons are
    // visually higher in the stacking order and remain clickable.
    function tacArea(points) {
      if (points.length < 2) return 0;
      var minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
      for (var i = 0; i < points.length; i++) {
        var p = points[i];
        if (p[0] < minLng) minLng = p[0];
        if (p[0] > maxLng) maxLng = p[0];
        if (p[1] < minLat) minLat = p[1];
        if (p[1] > maxLat) maxLat = p[1];
      }
      return (maxLng - minLng) * (maxLat - minLat);
    }

    // Sort: largest area first → small TACs end up on top of large ones
    var tacKeys = Object.keys(tacGroups).sort(function(a, b) {
      return tacArea(tacGroups[b]) - tacArea(tacGroups[a]);
    });

    tacKeys.forEach(function(tac, idx) {
      var points = tacGroups[tac];
      var color  = TAC_PALETTE[idx % TAC_PALETTE.length];
      var polygon = null;

      if (points.length === 1) {
        polygon = L.circleMarker([points[0][1], points[0][0]], {
          radius: 20, color: color, weight: 2,
          fillColor: color, fillOpacity: 0.08,
          interactive: true,
        });
      } else if (points.length === 2) {
        polygon = L.polyline([
          [points[0][1], points[0][0]],
          [points[1][1], points[1][0]],
        ], { color: color, weight: 2, opacity: 0.6, interactive: true });
      } else {
        var hull = convexHull(points);
        var latlngs = hull.map(function(p) { return [p[1], p[0]]; });
        polygon = L.polygon(latlngs, {
          color: color, weight: 2, opacity: 0.8,
          fillColor: color, fillOpacity: 0.08,
          dashArray: '5 5', interactive: true,
        });
      }

      polygon.addTo(layerGroup);

      // Click — toggle selection
      polygon.on('click', function() {
        var current = selectedTacRef.current;
        applySelection(current === tac ? null : tac);
      });

      // Centroid label
      var cLng = points.reduce(function(s, p) { return s + p[0]; }, 0) / points.length;
      var cLat = points.reduce(function(s, p) { return s + p[1]; }, 0) / points.length;

      var marker = L.marker([cLat, cLng], {
        icon: L.divIcon({
          className: '',
          html: '<div style="'
              + 'color:#1a1a1a;'
              + 'font-family:IBM Plex Mono,monospace;font-size:11px;font-weight:800;'
              + 'white-space:nowrap;pointer-events:none;'
              + 'letter-spacing:0.5px;'
              + 'text-shadow:0 0 3px #fff,0 0 3px #fff,0 0 3px #fff;'
              + 'transform:translate(-50%,-50%);'
              + '">' + tac + '</div>',
          iconAnchor: [0, 0],
        }),
        interactive: false,
      }).addTo(layerGroup);

      tacLayersRef.current[tac] = { polygon: polygon, marker: marker };
    });

    return function() {
      if (layerGroupRef.current) {
        layerGroupRef.current.clearLayers();
        map.removeLayer(layerGroupRef.current);
        layerGroupRef.current  = null;
        tacLayersRef.current   = {};
        selectedTacRef.current = null;
      }
    };
  }, [map, sites, visible, selectedTitles]);

  return null;
};

export default TacLayer;